import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toastr from "toastr";
import {
  fetchBookingWorkerRequests,
  fetchClientSuggestedRemainingWorkers,
  fetchClientSuggestedRemainingWorkersSession,
  useManualInviteWorkers,
  useRequestClientInvoice,
  useRejectClientWorker,
} from "../../../../api/booking";
import {
  getStep1ActionKey,
  getWorkerAvailableDates,
  getWorkerSlug,
  normalizeClientBookingPayload,
} from "../step1/useStep1SuggestedWorkers";
import {
  buildShiftDateCountsFromFinalDraft,
  buildShiftDateCountsFromRequests,
  getOverCapDatesForSelection,
  getShiftInviteCap,
  mergeShiftDateCounts,
} from "../inviteCaps";

export const REMAINING_LOAD_MORE_LIMIT = 5;

export const WORKER_REQUEST_FILTERS = {
  accepted: "accepted",
  previouslyRejected: "previouslyRejected",
  rejected: "rejected",
  invited: "invited",
};

/** Session id for suggested-remaining-workers restore (backend typo + corrected spelling). */
export function getSuggestedRemainingWorkerSessionId(pattern) {
  if (!pattern || typeof pattern !== "object") return null;
  return (
    pattern.suggestedRemaingWorkerSessionId ??
    pattern.suggestedRemainingWorkerSessionId ??
    null
  );
}

function getSuggestedRemainingWorkerSessionIdFromPayload(payload, shiftIndex) {
  if (!payload || typeof payload !== "object") return null;
  const topLevel =
    payload.sessionId ??
    payload.suggestedRemaingWorkerSessionId ??
    payload.suggestedRemainingWorkerSessionId;
  if (topLevel) return String(topLevel);
  const shift = payload.shiftPattern?.[shiftIndex];
  return getSuggestedRemainingWorkerSessionId(shift);
}

function dedupeWorkers(workers) {
  const seen = new Set();
  const out = [];
  for (const w of workers) {
    const slug = getWorkerSlug(w);
    const key = slug ?? JSON.stringify(w);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(w);
  }
  return out;
}

function getRemainingWorkerArray(payload) {
  if (!payload || payload.shiftPattern) return null;
  const list = payload.suggestedWorkers ?? payload.workers;
  return Array.isArray(list) ? list : null;
}

function extractRemainingShiftSlice(
  res,
  shiftIndex,
  { inferHasMoreFromChunkSize = false } = {}
) {
  const payload = normalizeClientBookingPayload(res);
  if (!payload) {
    return {
      workers: [],
      hasMore: false,
      matchingStatus: null,
      totalRecords: undefined,
    };
  }

  const flat = getRemainingWorkerArray(payload);
  if (flat) {
    const workers = flat;
    const totalRecords =
      typeof payload.totalRecords === "number"
        ? payload.totalRecords
        : undefined;
    let hasMore = false;
    if (totalRecords != null) {
      hasMore = workers.length < totalRecords;
    } else if (payload.hasMore === true) hasMore = true;
    else if (payload.hasMore === false) hasMore = false;
    else if (inferHasMoreFromChunkSize)
      hasMore = workers.length >= REMAINING_LOAD_MORE_LIMIT;
    return {
      workers,
      hasMore,
      matchingStatus:
        payload.suggestedWorkersMatchingStatus ??
        payload.matchingStatus ??
        null,
      totalRecords,
    };
  }

  const pat = payload.shiftPattern?.[shiftIndex];
  if (!pat) {
    return {
      workers: [],
      hasMore: false,
      matchingStatus: null,
      totalRecords: undefined,
    };
  }
  const workers = pat.workers ?? [];
  let hasMore = false;
  if (pat.hasMore === true) hasMore = true;
  else if (pat.hasMore === false) hasMore = false;
  else if (inferHasMoreFromChunkSize)
    hasMore = workers.length >= REMAINING_LOAD_MORE_LIMIT;
  return {
    workers,
    hasMore,
    matchingStatus: pat.suggestedWorkersMatchingStatus ?? null,
    totalRecords: undefined,
  };
}

export function parseWorkerRequestsPage(res) {
  if (!res || typeof res !== "object") return { items: [], total: 0 };
  /** Typical: `{ message, data: [...], total }` from `apiClient` (axios `response.data`). */
  if (Array.isArray(res.data)) {
    const items = res.data;
    const total = typeof res.total === "number" ? res.total : items.length;
    return { items, total };
  }
  const root = res?.data?.data ?? res?.data ?? res;
  if (Array.isArray(root)) {
    const total = typeof res.total === "number" ? res.total : root.length;
    return { items: root, total };
  }
  const items = Array.isArray(root?.data) ? root.data : [];
  const total = typeof root?.total === "number" ? root.total : items.length;
  return { items, total };
}

export function getRequestWorkerSlug(request) {
  if (!request) return null;
  return (
    request.user?.slug ||
    request.workerSlug ||
    request.worker?.user?.slug ||
    null
  );
}

/** Dates from workerShift rows (ISO `date` field). */
export function getRequestShiftDates(request) {
  const ws = request?.workerShift;
  if (!Array.isArray(ws)) return [];
  return ws.map((x) => x?.date).filter(Boolean);
}

export function getAcceptedDatesFromRequest(request) {
  const ws = request?.workerShift;
  if (!Array.isArray(ws)) return [];
  return ws
    .filter((x) => {
      if (!x?.date) return false;
      const status = String(x?.status || "").toLowerCase();
      return status === "accepted" || status === "approved";
    })
    .map((x) => x.date);
}

function flattenWorkerRequestQueryData(queryData) {
  if (!queryData?.pages?.length) return [];
  return queryData.pages.flatMap((page) => parseWorkerRequestsPage(page).items);
}

/** Workers required to staff a shift (`pattern.quantity`), not the invite cap. */
function getShiftStaffingQuantity(pattern) {
  const qty = Number(pattern?.quantity ?? 0);
  if (!Number.isFinite(qty) || qty < 0) return 0;
  return qty;
}

/** Distinct accepted workers per `shiftIndex` from worker-request rows. */
function buildAcceptedWorkerCountByShift(requests) {
  const byShift = new Map();
  for (const req of requests) {
    const shiftIndex = Number(req?.shiftIndex);
    if (!Number.isFinite(shiftIndex)) continue;
    const slug = getRequestWorkerSlug(req);
    if (!slug) continue;
    if (!byShift.has(shiftIndex)) byShift.set(shiftIndex, new Set());
    byShift.get(shiftIndex).add(slug);
  }
  const out = {};
  for (const [k, set] of byShift) out[k] = set.size;
  return out;
}

function useWorkerRequestsInfinite(
  bookingSlug,
  filter,
  pageSize = 10,
  enabled = true
) {
  return useInfiniteQuery({
    queryKey: ["booking-worker-requests", bookingSlug, filter, pageSize],
    queryFn: ({ pageParam }) =>
      fetchBookingWorkerRequests({
        bookingSlug,
        filter,
        page: pageParam,
        limit: pageSize,
      }),
    initialPageParam: 1,
    enabled: !!bookingSlug && !!filter && enabled,
    getNextPageParam: (lastPage, allPages) => {
      const { items: lastItems, total } = parseWorkerRequestsPage(lastPage);
      const loaded = allPages.reduce(
        (sum, p) => sum + parseWorkerRequestsPage(p).items.length,
        0
      );
      if (!lastItems.length || loaded >= total) return undefined;
      return allPages.length + 1;
    },
  });
}

export function useStep3Review({ booking, bookingSlug, refetchBooking }) {
  const queryClient = useQueryClient();
  const rejectMut = useRejectClientWorker();
  const inviteMut = useManualInviteWorkers();
  const requestInvoiceMut = useRequestClientInvoice();

  const patternsLen = booking?.shiftPattern?.length ?? 0;
  const isFinalInvited = booking?.isFinalInvited === true;
  const bookingRef = useRef(booking);
  bookingRef.current = booking;

  const [bootstrapKey, setBootstrapKey] = useState(0);
  const [byShift, setByShift] = useState({});
  const [selected, setSelected] = useState({});
  const [loadMorePending, setLoadMorePending] = useState({});
  const [initialPending, setInitialPending] = useState({});
  const [pendingRejectKey, setPendingRejectKey] = useState(null);
  const [remainingSessionByShift, setRemainingSessionByShift] = useState({});

  /** @type {Record<number, Array<{ workerSlug: string, availableDates: string[] }>>} */
  const [finalDraftByShift, setFinalDraftByShift] = useState({});

  const [inviteConfirmOpen, setInviteConfirmOpen] = useState(false);

  const byShiftRef = useRef({});
  useEffect(() => {
    byShiftRef.current = byShift;
  }, [byShift]);

  const initDoneRef = useRef(new Set());
  const resetInitForBootstrap = useCallback(() => {
    initDoneRef.current = new Set();
  }, []);

  useEffect(() => {
    resetInitForBootstrap();
  }, [bootstrapKey, resetInitForBootstrap]);

  useEffect(() => {
    setPendingRejectKey(null);
  }, [bootstrapKey]);

  const markInitialPending = useCallback((shiftIndex, pending) => {
    setInitialPending((prev) => ({ ...prev, [shiftIndex]: pending }));
  }, []);

  const applyShiftSlice = useCallback(
    (shiftIndex, res, { append } = { append: false }) => {
      const payload = normalizeClientBookingPayload(res);
      const responseSessionId = getSuggestedRemainingWorkerSessionIdFromPayload(
        payload,
        shiftIndex
      );
      if (responseSessionId) {
        setRemainingSessionByShift((prev) => {
          if (prev[shiftIndex] === responseSessionId) return prev;
          return { ...prev, [shiftIndex]: responseSessionId };
        });
      }

      const slice = extractRemainingShiftSlice(res, shiftIndex, {
        inferHasMoreFromChunkSize: append,
      });
      const { workers: chunk, matchingStatus, totalRecords } = slice;
      let { hasMore } = slice;

      setByShift((prev) => {
        const prevRow = prev[shiftIndex] || {};
        const base = append ? prevRow.workers ?? [] : [];
        const merged = dedupeWorkers([...base, ...chunk]);
        if (totalRecords != null) {
          hasMore = merged.length < totalRecords;
        }
        return {
          ...prev,
          [shiftIndex]: {
            workers: merged,
            hasMore,
            matchingStatus: matchingStatus ?? prevRow.matchingStatus ?? null,
            initialLoaded: true,
          },
        };
      });
    },
    []
  );

  const fetchRemainingPage = useCallback(
    (shiftIndex, { append }) => {
      if (isFinalInvited) {
        if (append) {
          setLoadMorePending((p) => ({ ...p, [shiftIndex]: false }));
        } else {
          markInitialPending(shiftIndex, false);
        }
        return;
      }
      const req = append
        ? fetchClientSuggestedRemainingWorkers({
            bookingSlug,
            shiftIndex,
            limit: REMAINING_LOAD_MORE_LIMIT,
          })
        : fetchClientSuggestedRemainingWorkers({ bookingSlug, shiftIndex });

      req
        .then((res) => {
          applyShiftSlice(shiftIndex, res, { append });
          if (!append) markInitialPending(shiftIndex, false);
        })
        .catch(() => {
          if (!append) {
            markInitialPending(shiftIndex, false);
            setByShift((prev) => ({
              ...prev,
              [shiftIndex]: {
                ...(prev[shiftIndex] || {}),
                workers: [],
                hasMore: false,
                initialLoaded: true,
                suggestedFetchError: true,
              },
            }));
            toastr.error("Could not load suggested remaining workers.");
          } else {
            toastr.error("Could not load more workers.");
          }
        })
        .finally(() => {
          if (append) {
            setLoadMorePending((p) => ({ ...p, [shiftIndex]: false }));
          }
        });
    },
    [applyShiftSlice, bookingSlug, isFinalInvited, markInitialPending]
  );

  useEffect(() => {
    const snap = bookingRef.current;
    if (!bookingSlug || !snap?.shiftPattern?.length || isFinalInvited) return;

    const len = snap.shiftPattern.length;
    for (let shiftIndex = 0; shiftIndex < len; shiftIndex++) {
      const initKey = `${bootstrapKey}:${shiftIndex}`;
      if (initDoneRef.current.has(initKey)) continue;

      const pattern = snap.shiftPattern[shiftIndex];
      const sessionId =
        getSuggestedRemainingWorkerSessionId(pattern) ??
        remainingSessionByShift[shiftIndex];

      if (sessionId) {
        initDoneRef.current.add(initKey);
        markInitialPending(shiftIndex, true);
        fetchClientSuggestedRemainingWorkersSession(sessionId)
          .then((res) => {
            applyShiftSlice(shiftIndex, res, { append: false });
            markInitialPending(shiftIndex, false);
          })
          .catch(() => {
            markInitialPending(shiftIndex, false);
            setByShift((prev) => ({
              ...prev,
              [shiftIndex]: {
                ...(prev[shiftIndex] || {}),
                workers: [],
                hasMore: false,
                initialLoaded: true,
                sessionError: true,
              },
            }));
            toastr.error(
              "Could not restore suggested remaining workers session."
            );
          });
      } else {
        initDoneRef.current.add(initKey);
        markInitialPending(shiftIndex, true);
        fetchRemainingPage(shiftIndex, { append: false });
      }
    }
  }, [
    bookingSlug,
    patternsLen,
    bootstrapKey,
    fetchRemainingPage,
    markInitialPending,
    applyShiftSlice,
    remainingSessionByShift,
    isFinalInvited,
  ]);

  const loadMore = useCallback(
    (shiftIndex) => {
      if (isFinalInvited) return;
      if (!byShiftRef.current[shiftIndex]?.hasMore) return;
      setLoadMorePending((p) => ({ ...p, [shiftIndex]: true }));
      fetchRemainingPage(shiftIndex, { append: true });
    },
    [fetchRemainingPage, isFinalInvited]
  );

  const toggleDate = useCallback((shiftIndex, workerSlug, isoDate) => {
    setSelected((prev) => {
      const row = { ...(prev[shiftIndex] || {}) };
      const dates = new Set(row[workerSlug] || []);
      if (dates.has(isoDate)) dates.delete(isoDate);
      else dates.add(isoDate);
      row[workerSlug] = [...dates];
      return { ...prev, [shiftIndex]: row };
    });
  }, []);

  const selectAllDatesForWorker = useCallback((shiftIndex, workerSlug) => {
    if (!workerSlug) return;
    const workers = byShiftRef.current[shiftIndex]?.workers ?? [];
    const w = workers.find((x) => getWorkerSlug(x) === workerSlug);
    const allDates = w ? getWorkerAvailableDates(w) : [];
    setSelected((prev) => {
      const row = { ...(prev[shiftIndex] || {}) };
      row[workerSlug] = [...allDates];
      return { ...prev, [shiftIndex]: row };
    });
  }, []);

  const removeFromFinalDraft = useCallback((shiftIndex, workerSlug) => {
    setFinalDraftByShift((prev) => {
      const row = (prev[shiftIndex] || []).filter(
        (x) => x.workerSlug !== workerSlug
      );
      const next = { ...prev, [shiftIndex]: row };
      return next;
    });
  }, []);

  const rejectWorker = useCallback(
    (shiftIndex, workerSlug, { rejectScope = "full_shift", dates } = {}) => {
      if (!workerSlug) return;
      if (
        rejectScope === "selected_dates" &&
        (!Array.isArray(dates) || !dates.length)
      ) {
        toastr.warning("Select at least one date to reject.");
        return;
      }
      const key = getStep1ActionKey(shiftIndex, workerSlug);
      setPendingRejectKey(key);
      const payload = {
        bookingSlug,
        shiftIndex,
        workerSlug,
        rejectScope,
      };
      if (rejectScope === "selected_dates") {
        payload.dates = dates;
      }
      rejectMut.mutate(payload, {
        onSettled: () => {
          setPendingRejectKey((cur) => (cur === key ? null : cur));
        },
        onSuccess: () => {
          toastr.success(
            rejectScope === "selected_dates"
              ? "Selected dates were rejected for this worker."
              : "Worker rejected for this shift."
          );
          setByShift((prev) => {
            const row = prev[shiftIndex];
            if (!row?.workers?.length) return prev;
            return {
              ...prev,
              [shiftIndex]: {
                ...row,
                workers: row.workers.filter(
                  (w) => getWorkerSlug(w) !== workerSlug
                ),
              },
            };
          });
          queryClient.invalidateQueries({
            queryKey: ["booking-worker-requests", bookingSlug],
          });
          refetchBooking?.();
        },
      });
    },
    [bookingSlug, queryClient, refetchBooking, rejectMut]
  );

  const manualInvitePayload = useMemo(() => {
    const shifts = Object.entries(finalDraftByShift)
      .map(([idx, workers]) => ({
        shiftIndex: Number(idx),
        workers: (workers || []).map((w) => ({
          workerSlug: w.workerSlug,
          availableDates: w.availableDates,
        })),
      }))
      .filter((s) => s.workers.length > 0);
    return { shifts };
  }, [finalDraftByShift]);

  const acceptedQ = useWorkerRequestsInfinite(
    bookingSlug,
    WORKER_REQUEST_FILTERS.accepted,
    10,
    true
  );

  const prevRejectedQ = useWorkerRequestsInfinite(
    bookingSlug,
    WORKER_REQUEST_FILTERS.previouslyRejected,
    10,
    !isFinalInvited
  );
  const rejectedQ = useWorkerRequestsInfinite(
    bookingSlug,
    WORKER_REQUEST_FILTERS.rejected,
    10,
    true
  );
  const invitedQ = useWorkerRequestsInfinite(
    bookingSlug,
    WORKER_REQUEST_FILTERS.invited,
    10,
    true
  );

  const acceptedRequests = useMemo(
    () => flattenWorkerRequestQueryData(acceptedQ.data),
    [acceptedQ.data]
  );
  const invitedRequests = useMemo(
    () => flattenWorkerRequestQueryData(invitedQ.data),
    [invitedQ.data]
  );

  const existingDateOccupancy = useMemo(
    () =>
      mergeShiftDateCounts(
        buildShiftDateCountsFromRequests(acceptedRequests),
        buildShiftDateCountsFromRequests(invitedRequests)
      ),
    [acceptedRequests, invitedRequests]
  );

  const getSelectionOverCapDates = useCallback(
    (
      shiftIndex,
      workerSlug,
      selectedDates,
      draftSource = finalDraftByShift
    ) => {
      const cap = getShiftInviteCap(booking?.shiftPattern?.[shiftIndex]);
      if (cap <= 0) return [];
      const draftCounts = buildShiftDateCountsFromFinalDraft(draftSource, {
        [shiftIndex]: workerSlug,
      });
      const counts = mergeShiftDateCounts(existingDateOccupancy, draftCounts);
      return getOverCapDatesForSelection({
        shiftIndex,
        selectedDates,
        cap,
        countsByShiftDate: counts,
      });
    },
    [booking?.shiftPattern, existingDateOccupancy, finalDraftByShift]
  );

  const addToFinalDraft = useCallback(
    (shiftIndex, workerSlug, availableDates) => {
      if (!workerSlug || !availableDates?.length) {
        toastr.warning(
          "Select at least one date to add this worker to your final invite."
        );
        return;
      }
      const overDates = getSelectionOverCapDates(
        shiftIndex,
        workerSlug,
        availableDates
      );
      if (overDates.length > 0) {
        toastr.warning(
          `Shift ${shiftIndex + 1} exceeds max capacity on ${overDates
            .map((d) => String(d).slice(0, 10))
            .join(", ")}.`
        );
        return;
      }
      setFinalDraftByShift((prev) => {
        const row = [...(prev[shiftIndex] || [])];
        const idx = row.findIndex((x) => x.workerSlug === workerSlug);
        const entry = { workerSlug, availableDates: [...availableDates] };
        if (idx >= 0) row[idx] = entry;
        else row.push(entry);
        return { ...prev, [shiftIndex]: row };
      });
      setSelected((prev) => {
        const row = { ...(prev[shiftIndex] || {}) };
        delete row[workerSlug];
        return { ...prev, [shiftIndex]: row };
      });
      toastr.success("Added to final invite.");
    },
    [getSelectionOverCapDates]
  );

  const overCapDateIssues = useMemo(() => {
    const allCounts = mergeShiftDateCounts(
      existingDateOccupancy,
      buildShiftDateCountsFromFinalDraft(finalDraftByShift)
    );
    const issues = [];
    for (const [rawShiftIndex, byDate] of Object.entries(allCounts)) {
      const shiftIndex = Number(rawShiftIndex);
      const cap = getShiftInviteCap(booking?.shiftPattern?.[shiftIndex]);
      if (cap <= 0) continue;
      for (const [isoDate, count] of Object.entries(byDate || {})) {
        if (Number(count) > cap) {
          issues.push({ shiftIndex, isoDate, count: Number(count), cap });
        }
      }
    }
    return issues;
  }, [booking?.shiftPattern, existingDateOccupancy, finalDraftByShift]);

  const { requestInvoiceEligibleByAcceptance, acceptanceOverallocation } =
    useMemo(() => {
      const shiftPatterns = booking?.shiftPattern ?? [];
      const counts = buildAcceptedWorkerCountByShift(acceptedRequests);

      let hasPositiveNeed = false;
      let eligibleByAcceptance = true;

      for (let i = 0; i < shiftPatterns.length; i++) {
        const need = getShiftStaffingQuantity(shiftPatterns[i]);
        if (need > 0) hasPositiveNeed = true;
        const have = counts[i] ?? 0;
        if (need > 0 && have < need) eligibleByAcceptance = false;
      }

      let overallocation = null;
      for (let i = 0; i < shiftPatterns.length; i++) {
        const need = getShiftStaffingQuantity(shiftPatterns[i]);
        const have = counts[i] ?? 0;
        if (need > 0 && have > need) {
          overallocation = { shiftIndex: i, required: need, accepted: have };
          break;
        }
      }

      return {
        requestInvoiceEligibleByAcceptance:
          hasPositiveNeed && eligibleByAcceptance,
        acceptanceOverallocation: overallocation,
      };
    }, [booking?.shiftPattern, acceptedRequests]);

  const canSendFinalInvites =
    manualInvitePayload.shifts.length > 0 && overCapDateIssues.length === 0;

  const sendFinalInvites = useCallback(() => {
    if (manualInvitePayload.shifts.length === 0) {
      toastr.warning(
        "Add at least one worker with dates to your final invite before sending."
      );
      return;
    }
    if (overCapDateIssues.length > 0) {
      const firstIssue = overCapDateIssues[0];
      toastr.warning(
        `Shift ${firstIssue.shiftIndex + 1} on ${
          firstIssue.isoDate
        } is over capacity (${firstIssue.count}/${firstIssue.cap}).`
      );
      return;
    }
    inviteMut.mutate(
      { bookingSlug, payload: manualInvitePayload },
      {
        onSuccess: () => {
          toastr.success("Final invites sent.");
          setFinalDraftByShift({});
          setSelected({});
          setByShift({});
          setBootstrapKey((k) => k + 1);
          setInviteConfirmOpen(false);
          refetchBooking?.();
        },
      }
    );
  }, [
    bookingSlug,
    inviteMut,
    manualInvitePayload,
    overCapDateIssues,
    refetchBooking,
  ]);

  const listBusy = rejectMut.isPending || inviteMut.isPending;

  const requestInvoice = useCallback(() => {
    if (!bookingSlug) return;
    if (acceptanceOverallocation) {
      const { shiftIndex, required, accepted } = acceptanceOverallocation;
      toastr.warning(
        `First remove overallocated workers. Shift ${
          shiftIndex + 1
        } only requires ${required} worker${
          required === 1 ? "" : "s"
        }; you have ${accepted} accepted.`
      );
      return;
    }
    requestInvoiceMut.mutate(bookingSlug, {
      onSuccess: () => {
        toastr.success("Invoice request submitted.");
      },
    });
  }, [bookingSlug, requestInvoiceMut, acceptanceOverallocation]);

  return {
    byShift,
    selected,
    loadMore,
    loadMorePending,
    initialPending,
    toggleDate,
    selectAllDatesForWorker,
    addToFinalDraft,
    finalDraftByShift,
    removeFromFinalDraft,
    rejectWorker,
    pendingRejectKey,
    listBusy,
    inviteMutation: inviteMut,
    rejectMutation: rejectMut,
    canSendFinalInvites,
    sendFinalInvites,
    getSelectionOverCapDates,
    overCapDateIssues,
    inviteConfirmOpen,
    setInviteConfirmOpen,
    manualInvitePayload,
    acceptedQ,
    prevRejectedQ,
    rejectedQ,
    invitedQ,
    isFinalInvited,
    requestInvoice,
    requestInvoiceMutation: requestInvoiceMut,
    requestInvoiceEligibleByAcceptance,
    acceptanceOverallocation,
  };
}
